import { useState, useRef } from 'react'
import { X, Shield, Plus, StickyNote } from 'lucide-react'
import type { CascadeNode as CascadeNodeType } from '../types'
import { NODE_COLORS, DECISION_COLORS, CAPTURE_METHOD_TYPE_CONFIG, VALIDATION_RULESET_CONFIG } from '../types'
import { useCascadeStore } from '../store'
import { getChoicesForNode } from '../data'

interface Props {
  node: CascadeNodeType
  isSelected: boolean
  isConnecting: boolean
  zoom: number
}

export function CascadeNode({ node, isSelected, isConnecting, zoom }: Props) {
  const {
    selectNode, removeNode, moveNode, setDragging,
    startConnecting, finishConnecting, connectingFrom,
    showChoices, activeChoice, addSecondaryVendor,
  } = useCascadeStore()

  const nodeRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragOffset = useRef({ x: 0, y: 0 })

  const colors = NODE_COLORS[node.type] || NODE_COLORS['validation']
  const isCustomer = node.type === 'customer-input'
  const isDecision = node.type === 'decision'
  const isForm = node.type === 'capture-method' && node.captureMethodType === 'form'
  const isValidation = node.type === 'validation'
  const hasChoices = getChoicesForNode(node.type).length > 0
  const isShowingChoices = activeChoice?.sourceNodeId === node.id

  // Map node types to PNG images (null = use emoji icon instead)
  const nodeImage = isCustomer ? '/woman.png'
    : isForm ? '/form.png'
    : isValidation ? '/policy.png'
    : null

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-action]')) return
    e.stopPropagation()

    // If we're in free-connect mode, finish the connection to this node
    if (connectingFrom && connectingFrom !== node.id) {
      finishConnecting(node.id)
      return
    }

    setIsDragging(true)
    setDragging(node.id)
    selectNode(node.id)

    const rect = nodeRef.current?.parentElement?.getBoundingClientRect()
    if (rect) {
      dragOffset.current = {
        x: e.clientX / zoom - node.x,
        y: e.clientY / zoom - node.y,
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX / zoom - dragOffset.current.x
      const newY = e.clientY / zoom - dragOffset.current.y
      moveNode(node.id, Math.round(newX), Math.round(newY))
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      setDragging(null)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
  }

  const handleConnect = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasChoices) {
      // Guided mode: show contextual choices
      showChoices(node.id)
    } else {
      // Free-connect mode for nodes without a choice tree
      startConnecting(node.id)
    }
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isCustomer) removeNode(node.id)
  }

  // Decision nodes get special outcome-based coloring
  const decisionCondition = node.sublabel as keyof typeof DECISION_COLORS | undefined
  const decisionColor = isDecision && decisionCondition && DECISION_COLORS[decisionCondition]

  return (
    <div
      ref={nodeRef}
      data-node
      data-node-id={node.id}
      className={`
        absolute select-none cursor-grab active:cursor-grabbing group
        ${isDragging ? 'z-50' : 'z-10'}
      `}
      style={{
        left: node.x,
        top: node.y,
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Hero icon — pops out above the card border */}
      {nodeImage ? (
        <div className="absolute left-1/2 -translate-x-1/2 -top-7 z-20 pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-white dark:bg-slate-700 shadow-lg border-2 border-white dark:border-slate-600 flex items-center justify-center overflow-hidden">
            <img
              src={nodeImage}
              alt={node.label}
              className="w-10 h-10 object-contain"
              draggable={false}
            />
          </div>
        </div>
      ) : (
        <div className="absolute left-1/2 -translate-x-1/2 -top-6 z-20 pointer-events-none">
          <div className={`w-12 h-12 rounded-full shadow-lg border-2 border-white dark:border-slate-600 flex items-center justify-center ${
            decisionColor ? decisionColor.bg : 'bg-white dark:bg-slate-700'
          }`}>
            <span className="text-2xl leading-none">
              {node.captureMethodType
                ? (CAPTURE_METHOD_TYPE_CONFIG[node.captureMethodType]?.icon || colors.icon)
                : colors.icon}
            </span>
          </div>
        </div>
      )}

      <div
        className={`
          relative rounded-xl border-2 shadow-lg transition-all duration-150 mt-7
          ${decisionColor ? `${decisionColor.bg} border-current` : `${colors.bg} ${colors.border}`}
          ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''}
          ${isConnecting && connectingFrom !== node.id ? 'ring-2 ring-green-400 ring-offset-1 ring-offset-[var(--bg-primary)] animate-pulse' : ''}
          ${isShowingChoices ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''}
          min-w-[140px] max-w-[200px]
        `}
      >
        {/* Node header — text centered under the hero icon */}
        <div className="px-3 py-2 pt-3 text-center">
          {node.type === 'validation' && node.validationVendor ? (
            <>
              <div className={`text-xs font-bold leading-tight ${colors.text}`}>
                {node.validationVendor}
              </div>
              <div className="text-[10px] text-[var(--text-secondary)] mt-0.5 leading-tight">
                Identity Check
              </div>
            </>
          ) : (
            <>
              <div className={`text-xs font-bold leading-tight ${decisionColor ? decisionColor.text : colors.text}`}>
                {node.label}
              </div>
              {node.sublabel && !isDecision && (
                <div className="text-[10px] text-[var(--text-secondary)] mt-0.5 leading-tight">
                  {node.sublabel}
                </div>
              )}
              {node.vendor && (
                <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                  {node.vendor}
                </div>
              )}
            </>
          )}
        </div>

        {/* Vendor pill — prominent when vendor-hosted with a vendor selected */}
        {node.type === 'capture-method' && node.captureOwner === 'vendor' && node.captureVendorName && (
          <div className="px-3 pb-1">
            <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-teal-500/25 text-teal-500 dark:text-teal-300 border border-teal-500/30">
              {node.captureVendorName}
            </span>
          </div>
        )}

        {/* Capture owner badge (for capture-method nodes) */}
        {node.type === 'capture-method' && (
          <div className="px-3 pb-1">
            <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${
              node.captureOwner === 'vendor'
                ? 'bg-teal-500/15 text-teal-600 dark:text-teal-400'
                : 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400'
            }`}>
              {node.captureOwner === 'vendor' ? 'Vendor-Hosted' : 'Client-Side'}
            </span>
          </div>
        )}

        {/* Collected field pills (for capture-method nodes) */}
        {node.collectedFields && node.collectedFields.some(f => f.enabled) && (
          <div className="px-3 pb-2 flex flex-wrap gap-1">
            {node.collectedFields.filter(f => f.enabled).map(f => {
              // Build display label with contextual details
              let displayLabel = f.label
              if (f.id === 'address' && f.addressMethod) {
                const methodLabels: Record<string, string> = { autofill: 'Autofill', 'broken-down': 'Broken Down', 'open-field': 'Open Field' }
                displayLabel = `${f.label} (${methodLabels[f.addressMethod] ?? f.addressMethod})`
              }
              if (f.id === 'phone' && f.possessionVerified) {
                displayLabel = `${f.label} (OTP)`
              }
              if (f.id === 'email' && f.possessionVerified) {
                displayLabel = `${f.label} (OTP)`
              }
              return (
                <span
                  key={f.id}
                  className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${
                    f.optional
                      ? 'bg-white/50 dark:bg-white/10 text-[var(--text-muted)]'
                      : 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
                  }`}
                >
                  {displayLabel}{!f.optional && <span className="text-red-500 ml-0.5">*</span>}
                </span>
              )
            })}
          </div>
        )}

        {/* Validation node: vendor + ruleset + verified fields */}
        {node.type === 'validation' && (
          <>
            <div className="px-3 pb-1 flex flex-wrap gap-1">
              {node.validationRuleset && (
                <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-semibold">
                  {(VALIDATION_RULESET_CONFIG as Record<string, { label: string }>)[node.validationRuleset]?.label || node.validationRuleset}
                </span>
              )}
              {node.validationVendor && (
                <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-white/50 dark:bg-white/10 text-[var(--text-secondary)] font-medium">
                  {node.validationVendor}
                </span>
              )}
            </div>
            {node.verifiedFields && node.verifiedFields.some(f => f.enabled) && (
              <div className="px-3 pb-2 flex flex-wrap gap-1">
                {node.verifiedFields.filter(f => f.enabled).map(f => (
                  <span
                    key={f.id}
                    className="text-[8px] px-1.5 py-0.5 rounded-full bg-white/50 dark:bg-white/10 text-[var(--text-secondary)] font-medium"
                  >
                    {f.label}
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {/* Verifies badges */}
        {node.verifies && node.verifies.length > 0 && (
          <div className="px-3 pb-2 flex flex-wrap gap-1">
            {node.verifies.map(v => (
              <span
                key={v}
                className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/50 dark:bg-white/10 text-[var(--text-secondary)] font-medium"
              >
                {v}
              </span>
            ))}
          </div>
        )}

        {/* Consent badge */}
        {node.requiresConsent && (
          <div className="px-3 pb-2">
            <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 font-medium">
              <Shield size={8} /> Consent
            </span>
          </div>
        )}

        {/* Price badge */}
        {node.price && (
          <div className="px-3 pb-2">
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-600 dark:text-green-400 font-semibold">
              {node.price}
            </span>
          </div>
        )}

        {/* Note indicator */}
        {node.nodeNotes && (
          <div className="absolute -top-2 -left-2 p-1 rounded-full bg-amber-500/80 text-white shadow-md" title={node.nodeNotes}>
            <StickyNote size={8} />
          </div>
        )}

        {/* Delete button (hover) */}
        {!isCustomer && (
          <button
            data-action="delete"
            onClick={handleDelete}
            className="absolute -top-2 -right-2 p-1 rounded-full bg-red-500 text-white shadow-md hover:bg-red-600 transition-all opacity-0 group-hover:opacity-100"
            title="Remove node"
          >
            <X size={10} />
          </button>
        )}

        {/* Connect / Next-step handle (right side) */}
        {hasChoices && (
          <button
            data-action="connect"
            onClick={handleConnect}
            className={`
              absolute top-1/2 -right-3.5 -translate-y-1/2
              w-7 h-7 rounded-full border-2 flex items-center justify-center
              shadow-md transition-all
              ${isShowingChoices
                ? 'bg-green-500 border-green-400 text-white scale-110 rotate-45'
                : 'bg-green-500 border-green-400 text-white hover:scale-110 hover:shadow-lg'
              }
            `}
            title={isShowingChoices ? 'Close choices' : 'What happens next?'}
          >
            <Plus size={12} strokeWidth={3} />
          </button>
        )}

        {/* Add vendor handle (bottom of validation nodes) */}
        {node.type === 'validation' && (
          <button
            data-action="add-vendor"
            onClick={(e) => {
              e.stopPropagation()
              addSecondaryVendor(node.id)
            }}
            className="absolute -bottom-3.5 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full border-2 flex items-center justify-center shadow-md transition-all bg-blue-500 border-blue-400 text-white hover:scale-110 hover:shadow-lg"
            title="Add another vendor"
          >
            <Plus size={10} strokeWidth={3} />
          </button>
        )}

        {/* Incoming connection target (left side) — hide on secondary vendor nodes */}
        {!isCustomer && !node.isSecondaryVendor && (
          <div
            className={`
              absolute top-1/2 -left-2 -translate-y-1/2
              w-4 h-4 rounded-full border-2
              ${isConnecting && connectingFrom !== node.id
                ? 'bg-green-400 border-green-500 animate-pulse'
                : 'bg-[var(--bg-tertiary)] border-[var(--border-color)]'
              }
            `}
          />
        )}
      </div>
    </div>
  )
}
